import React, { useEffect, useState, useCallback, useMemo } from 'react';
import useUIStore from '../../store/uiStore';
import * as cashflowService from '../../services/cashflowService';
import * as projectService from '../../services/projectService';
import CashFlowTabs from '../../components/CashFlowTabs';
import { BalanceChart, NetFlowChart, NET_FLOW_LEGEND } from '../../components/CashFlowChart';
import { downloadBlob } from '../../utils/downloadFile';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Weekly periods -> 4-week "monthly" buckets, purely a client-side
// re-bucketing of the same weekly data the backend returns (see
// cashflow_routes.py's module docstring on why this isn't a second
// backend code path).
const toMonthlyPeriods = (periods) => {
  const out = [];
  for (let i = 0; i < periods.length; i += 4) {
    const chunk = periods.slice(i, i + 4);
    const inflow = chunk.reduce((s, p) => s + p.inflow, 0);
    const outflow = chunk.reduce((s, p) => s + p.outflow, 0);
    const advance = chunk.reduce((s, p) => s + (p.inflow_breakdown?.advance || 0), 0);
    const other = chunk.reduce((s, p) => s + (p.inflow_breakdown?.other || 0), 0);
    const materials = chunk.reduce((s, p) => s + (p.outflow_breakdown?.materials || 0), 0);
    const site = chunk.reduce((s, p) => s + (p.outflow_breakdown?.site || 0), 0);
    const recurring = chunk.reduce((s, p) => s + (p.outflow_breakdown?.recurring || 0), 0);
    const post_dated = chunk.reduce((s, p) => s + (p.outflow_breakdown?.post_dated || 0), 0);
    out.push({
      week_label: `M${out.length + 1}`,
      start_date: chunk[0].start_date,
      end_date: chunk[chunk.length - 1].end_date,
      opening_balance: chunk[0].opening_balance,
      inflow, outflow, net: inflow - outflow,
      balance: chunk[chunk.length - 1].balance,
      inflow_breakdown: { advance, other },
      outflow_breakdown: { materials, site, recurring, post_dated },
    });
  }
  return out;
};

const BudgetBar = ({ label, spent, budget, note }) => {
  if (budget == null) {
    return (
      <div className="flex items-center gap-3 text-xs">
        <span className="w-20 text-gray-500">{label}</span>
        <span className="text-gray-400">No budget set - spent {money(spent)}</span>
      </div>
    );
  }
  const pct = budget > 0 ? spent / budget : 0;
  const color = pct > 1 ? 'bg-red-500' : pct > 0.9 ? 'bg-amber-500' : 'bg-komal-blue';
  return (
    <div>
      <div className="grid grid-cols-[70px_1fr_150px] items-center gap-2 text-xs">
        <span className="text-gray-500">{label}</span>
        <div className="bg-gray-100 rounded h-2">
          <div className={`h-2 rounded ${color}`} style={{ width: `${Math.min(pct, 1) * 100}%` }} />
        </div>
        <span className="text-gray-600 text-right">
          {money(spent)} / {money(budget)} <b className={pct > 1 ? 'text-red-600' : 'text-gray-900'}>{Math.round(pct * 100)}%</b>
        </span>
      </div>
      {note && <div className="text-[11px] text-gray-400 mt-0.5 pl-[78px]">{note}</div>}
    </div>
  );
};

// Labour has a derived budget (5% of the raw material budget, see
// split_material_and_labour_budget) but no tracked spend in this module -
// it forecasts requirements, not actuals, unlike the Expense module. Shown
// as a budget-only line rather than a spend bar, so it doesn't imply ₹0
// spent when spend simply isn't tracked here.
const LabourBudgetLine = ({ budget }) => {
  if (budget == null) return null;
  return (
    <div className="grid grid-cols-[70px_1fr] items-baseline gap-2 text-xs">
      <span className="text-gray-500">Labour</span>
      <span className="text-gray-400">{money(budget)} budgeted (5% of material budget) - spend tracked in the Expense module</span>
    </div>
  );
};

const CashFlowDashboardPage = () => {
  const showToast = useUIStore((s) => s.showToast);

  const [allProjects, setAllProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [forecast, setForecast] = useState(null);
  const [dailyForecast, setDailyForecast] = useState(null);
  const [summary, setSummary] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'day' | 'week' | 'month'
  const [isLoading, setIsLoading] = useState(false);
  const [isDailyLoading, setIsDailyLoading] = useState(false);
  const [showCalc, setShowCalc] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await projectService.getProjects();
        const projects = res.data.projects || [];
        setAllProjects(projects);
        setSelectedIds(new Set(projects.map((p) => p.id)));
      } catch (error) {
        showToast(error.response?.data?.detail || 'Failed to load projects', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopeParam = useMemo(
    () => (selectedIds.size > 0 ? [...selectedIds].join(',') : '0'), // '0' = no project matches, vs omitting the param which means "all"
    [selectedIds]
  );

  const loadForecast = useCallback(async () => {
    if (allProjects.length === 0) return;
    setIsLoading(true);
    try {
      const [forecastRes, summaryRes] = await Promise.all([
        cashflowService.getForecast({ weeks: 13, project_ids: scopeParam }),
        cashflowService.getProjectsSummary({ project_ids: scopeParam }),
      ]);
      setForecast(forecastRes.data);
      setSummary(summaryRes.data.projects || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load forecast', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeParam, allProjects.length]);

  useEffect(() => { loadForecast(); }, [loadForecast]);

  // Daily view is a separate call (granularity=day), not a client-side
  // re-bucketing of the weekly data - only fetched when actually selected,
  // so switching to Weekly/Monthly never pays for it.
  const loadDaily = useCallback(async () => {
    if (allProjects.length === 0) return;
    setIsDailyLoading(true);
    try {
      const res = await cashflowService.getForecast({ granularity: 'day', days: 30, project_ids: scopeParam });
      setDailyForecast(res.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load daily forecast', 'error');
    } finally {
      setIsDailyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeParam, allProjects.length]);

  useEffect(() => { if (viewMode === 'day') loadDaily(); }, [viewMode, loadDaily]);

  const toggleProject = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = allProjects.length > 0 && selectedIds.size === allProjects.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleAllProjects = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allProjects.map((p) => p.id)));
  };

  const handleExport = async () => {
    try {
      const res = await cashflowService.exportForecastCsv({ weeks: 13, project_ids: scopeParam });
      downloadBlob(res.data, 'cash_flow_forecast.csv');
    } catch (error) {
      showToast('Failed to export forecast', 'error');
    }
  };

  const periods = viewMode === 'day'
    ? (dailyForecast ? dailyForecast.periods : [])
    : forecast
      ? (viewMode === 'month' ? toMonthlyPeriods(forecast.periods) : forecast.periods)
      : [];
  const chartLoading = viewMode === 'day' ? (isDailyLoading || !dailyForecast) : (isLoading || !forecast);
  // KPI tiles always reflect the 13-week weekly forecast regardless of which
  // chart view is showing - the daily view is for reading composition day
  // by day, not for recomputing "available to commit" on a 1-day lookahead.
  const kpis = forecast?.kpis;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <CashFlowTabs />
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Cash Flow Forecast</h1>
            {forecast && (
              <p className="text-white/80 text-sm mt-1">
                As of {forecast.as_of_date} - <span className="font-semibold">{money(forecast.opening_cash)}</span> cash on hand
              </p>
            )}
          </div>
          <button onClick={handleExport} className="text-xs px-3 py-1.5 rounded border border-white/40 text-white hover:bg-white/10">
            Export CSV
          </button>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-card p-4">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Available to commit</div>
            <div className={`text-xl font-bold mt-1 ${kpis?.available_to_commit < 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {kpis ? money(kpis.available_to_commit) : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">this week, after buffer</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">13-wk low point</div>
            <div className={`text-xl font-bold mt-1 ${kpis?.low_point < 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {kpis ? money(kpis.low_point) : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{kpis ? `in W${kpis.low_point_week + 1}` : ''}</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Runway</div>
            <div className="text-xl font-bold mt-1 text-gray-900">
              {kpis ? (kpis.runway_week == null ? '13+ wks' : `W${kpis.runway_week + 1}`) : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">until balance turns negative</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Funding gap (4 wks)</div>
            <div className={`text-xl font-bold mt-1 ${kpis?.funding_gap_4wk > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {kpis ? money(kpis.funding_gap_4wk) : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">selected projects</div>
          </div>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-6 items-start">
          {/* Scenario panel */}
          <div className="glass-card p-4">
            <h2 className="font-semibold text-gray-900 mb-1">Scenario - projects in play</h2>
            <p className="text-xs text-gray-400 mb-3">Uncheck a project to see cash flow without it. Overheads are always included.</p>
            {allProjects.length > 0 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-2 mb-2 border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAllProjects}
                />
                <span className="font-medium text-gray-700">
                  {allSelected ? 'Deselect all' : 'Select all'} ({selectedIds.size}/{allProjects.length})
                </span>
              </label>
            )}
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
              {allProjects.map((p) => {
                const row = summary.find((s) => s.project_id === p.id);
                const gap = row?.four_week_gap;
                return (
                  <label key={p.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleProject(p.id)} className="mt-1" />
                    <span className="flex-1">
                      <span className="block font-medium text-gray-900">{p.name}</span>
                      {gap != null && (
                        <span className="block text-xs text-gray-400">
                          4-wk gap: <b className={gap > 0 ? 'text-red-600' : 'text-green-600'}>{money(gap)}</b>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
              {allProjects.length === 0 && <p className="text-xs text-gray-400">No active projects.</p>}
            </div>
          </div>

          {/* Charts + budgets */}
          <div className="space-y-6">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-semibold text-gray-900">Projected cash balance</h2>
                <div className="flex border border-gray-200 rounded overflow-hidden">
                  {['day', 'week', 'month'].map((m) => (
                    <button
                      key={m}
                      onClick={() => setViewMode(m)}
                      className={`text-xs px-3 py-1 font-semibold ${viewMode === m ? 'bg-komal-blue text-white' : 'bg-white text-gray-600'}`}
                    >
                      {m === 'day' ? 'Daily' : m === 'week' ? 'Weekly' : 'Monthly'}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                {viewMode === 'day'
                  ? 'Next 30 days, one bar per day - the KPI tiles above still reflect the full 13-week view.'
                  : 'Rolling balance across the selected scenario, with the safety buffer marked.'}
              </p>
              {chartLoading ? (
                <p className="text-sm text-gray-400 py-10 text-center">Loading...</p>
              ) : periods.length === 0 ? (
                <p className="text-sm text-gray-400 py-10 text-center">No data for this view.</p>
              ) : (
                <>
                  <BalanceChart periods={periods} bufferAmount={(forecast?.opening_cash || 0) * (forecast?.buffer_pct || 0)} />
                  <button
                    onClick={() => setShowCalc((v) => !v)}
                    className="mt-3 text-xs font-semibold text-komal-blue hover:underline"
                  >
                    {showCalc ? 'Hide calculation' : 'Show calculation'}
                  </button>
                  {showCalc && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500 border-b border-gray-100">
                            <th className="px-2 py-1.5 font-medium whitespace-nowrap">Period</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap">Opening balance</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-green-700">+ Advances</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-blue-700">+ Other receivables</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-red-700">- Materials</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-orange-700">- Site</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-purple-700">- Recurring</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap text-gray-600">- Post-dated</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap">= Closing balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periods.map((p) => (
                            <tr key={p.index ?? p.week_label} className="border-b border-gray-50 last:border-0">
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                {p.week_label}
                                {viewMode !== 'day' && (
                                  <span className="text-gray-400 ml-1">
                                    ({new Date(p.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap">{money(p.opening_balance)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-green-700">{money(p.inflow_breakdown?.advance)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-blue-700">{money(p.inflow_breakdown?.other)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-red-700">{money(p.outflow_breakdown?.materials)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-orange-700">{money(p.outflow_breakdown?.site)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-purple-700">{money(p.outflow_breakdown?.recurring)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap text-gray-600">{money(p.outflow_breakdown?.post_dated)}</td>
                              <td className={`px-2 py-1.5 text-right whitespace-nowrap font-semibold ${p.balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>{money(p.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-gray-400 mt-2">
                        Each row: opening balance + advances + other receivables - materials - site - recurring
                        expenses - post-dated items/cheques = closing balance, which becomes next period's opening
                        balance. Export CSV above includes the same columns.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="glass-card p-6">
              <h2 className="font-semibold text-gray-900 mb-1">
                {viewMode === 'day' ? 'Daily' : viewMode === 'month' ? 'Monthly' : 'Weekly'} cash flow - by source
              </h2>
              <p className="text-xs text-gray-400 mb-3">
                What's actually driving each {viewMode === 'day' ? 'day' : viewMode === 'month' ? 'month' : 'week'} -
                advances shown separately from other receivables, so a project advance doesn't disappear into the total. Hover a bar for the exact figure.
              </p>
              {chartLoading ? (
                <p className="text-sm text-gray-400 py-10 text-center">Loading...</p>
              ) : periods.length === 0 ? (
                <p className="text-sm text-gray-400 py-10 text-center">No data for this view.</p>
              ) : (
                <NetFlowChart periods={periods} />
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs text-gray-600">
                {NET_FLOW_LEGEND.map((item) => (
                  <span key={item.label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: item.color }} />
                    {item.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="glass-card p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Budget vs actual - by project</h2>
              {summary.length === 0 ? (
                <p className="text-sm text-gray-400">No projects in the current scenario.</p>
              ) : (
                <div className="space-y-4">
                  {summary.map((s) => (
                    <div key={s.project_id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                      <div className="font-medium text-sm text-gray-900 mb-2">{s.project_name}</div>
                      <div className="space-y-1.5">
                        {/* Material's budget is already the reduced 95%
                            figure - 5% of the raw allotted budget is
                            reserved for labour below (2026-09-29 user
                            instruction, same split the Expense dashboard
                            uses so the two agree). */}
                        <BudgetBar
                          label="Material"
                          spent={s.material_spent}
                          budget={s.budgeted_material_cost}
                          note={s.budgeted_labour_cost != null ? `${money(s.budgeted_labour_cost)} of the original budget reserved for labour, below` : undefined}
                        />
                        <BudgetBar label="Site install" spent={s.site_spent} budget={s.budgeted_site_cost} />
                        <LabourBudgetLine budget={s.budgeted_labour_cost} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowDashboardPage;
